import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, User, Tag, ArrowLeft, Share2, Facebook, Twitter } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDate, CATEGORY_COLORS } from "@/lib/helpers";
import { Button } from "@/components/ui/button";

export default function NewsDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/news/${slug}`).then((r) => {
      setItem(r.data);
      api.get(`/news?category=${encodeURIComponent(r.data.category)}&limit=3`).then((rr) =>
        setRelated(rr.data.items.filter((n) => n.slug !== slug).slice(0, 2)));
    }).catch(() => setItem(false));
  }, [slug]);

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Tautan berita disalin ke clipboard");
  };

  if (item === false) return <div className="container-page py-24 text-center text-slate-500">Berita tidak ditemukan.</div>;
  if (!item) return <div className="container-page py-24 text-center text-slate-400">Memuat…</div>;

  return (
    <div className="container-page py-10 max-w-4xl">
      <Link to="/info" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brandred mb-6" data-testid="back-to-info">
        <ArrowLeft className="h-4 w-4" /> Kembali ke Info Bawaslu
      </Link>
      <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full ${CATEGORY_COLORS[item.category] || "bg-navy/5 text-navy"}`}>{item.category}</span>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-navy mt-4 leading-tight" data-testid="news-title">{item.title}</h1>
      <div className="flex flex-wrap items-center gap-5 text-sm text-slate-500 mt-4 pb-6 border-b border-slate-200">
        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(item.date)}</span>
        <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> {item.author}</span>
      </div>
      <img src={item.thumbnail} alt={item.title} className="w-full h-72 sm:h-96 object-cover rounded-xl mt-6" />
      <div className="prose-news mt-8 text-[17px]" dangerouslySetInnerHTML={{ __html: item.content }} />

      {item.gallery?.length > 0 && (
        <div className="mt-8">
          <h3 className="font-bold text-navy mb-3">Galeri</h3>
          <div className="grid grid-cols-2 gap-3">
            {item.gallery.map((g, i) => <img key={i} src={g} alt={`galeri ${i + 1}`} className="rounded-lg h-40 w-full object-cover" />)}
          </div>
        </div>
      )}

      {item.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8">
          {item.tags.map((t) => <span key={t} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full"><Tag className="h-3 w-3" />{t}</span>)}
        </div>
      )}

      <div className="flex items-center gap-3 mt-8 pt-6 border-t border-slate-200">
        <span className="text-sm font-semibold text-slate-600 flex items-center gap-2"><Share2 className="h-4 w-4" /> Bagikan:</span>
        <Button size="sm" variant="outline" onClick={share} data-testid="share-btn"><Facebook className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" onClick={share}><Twitter className="h-4 w-4" /></Button>
        <Button size="sm" variant="outline" onClick={share}>Salin Tautan</Button>
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <h3 className="font-bold text-navy text-lg mb-4">Berita Terkait</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {related.map((r) => (
              <Link key={r.id} to={`/info/${r.slug}`} className="flex gap-3 bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md transition">
                <img src={r.thumbnail} alt={r.title} className="h-16 w-16 rounded object-cover shrink-0" />
                <div><div className="text-xs text-slate-400">{formatDate(r.date)}</div><h4 className="text-sm font-bold text-navy line-clamp-2">{r.title}</h4></div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
